import React from "react";
import {
    createListTableColumns,
    ContentSection,
    ListTable,
    Text,
    Stack,
    SuccessMessage,
    ensure,
    ListTableColumn,
    renderTextWithTooltip
} from "sprint-common-web";

import { convertTimestampToTimestring } from "../../../../../../common/utils/convertTimestampToTimestring";
import { useCloudAssetDetails } from "../../../common/hooks/useCloudAssetDetails";
import { ProxyItem } from "./ProxyItem";
import { useCloudAssetProxies } from "./useCloudAsssetProxies";

export const ProxyAssetsSection: React.FunctionComponent = () => {
    const { data: assetDetails } = useCloudAssetDetails();
    const { data: proxies, isLoading } = useCloudAssetProxies(assetDetails?.cloudFileId);

    const isTrimmed = proxies?.find((p) => p.details);

    const columns = createProxyAssetsColumns();

    return (
        <ContentSection header="Proxy Assets" isLoading={isLoading} testId="proxy-assets-content-section">
            {isTrimmed && <SuccessMessage message="Trimmed assets" />}
            <ListTable columns={columns} items={proxies ?? []} isLoading={isLoading} />
        </ContentSection>
    );
};

const createProxyAssetsColumns = (): ListTableColumn<ProxyItem>[] => {
    return createListTableColumns<ProxyItem>([
        {
            type: "basic",
            key: "encoding",
            name: "Encoding",
            minWidth: 100,
            maxWidth: 200,
            onRender: (item) => {
                ensure(item);
                const props = {
                    "data-testid": item.encoding
                };
                return <span {...props}>{item.encoding}</span>;
            }
        },
        {
            type: "basic",
            key: "filePath",
            name: "File path",
            minWidth: 150,
            maxWidth: 500,
            onRender: renderTextWithTooltip
        },
        {
            type: "basic",
            key: "fileName",
            name: "File name",
            minWidth: 150,
            maxWidth: 500,
            onRender: renderTextWithTooltip
        },
        {
            type: "basic",
            key: "details",
            name: "Details",
            onRender: renderDetails,
            minWidth: 100
        }
    ]);
};

const renderDetails = (item?: ProxyItem) => {
    const details = item?.details;

    if (!details) {
        return <Text value="-" />;
    }

    const startTrimmingTime = details.trimming.startTrimmingPointTimestamp
        ? convertTimestampToTimestring(details.trimming.startTrimmingPointTimestamp)
        : "";
    const endTrimmingTime = details.trimming.endTrimmingPointTimestamp
        ? convertTimestampToTimestring(details.trimming.endTrimmingPointTimestamp)
        : "";

    return (
        <Stack>
            {startTrimmingTime && (
                <Text variant={"small"} value={"Trimming point: Start - " + startTrimmingTime}></Text>
            )}
            {endTrimmingTime && <Text variant={"small"} value={"Trimming point: " + endTrimmingTime + "- End"}></Text>}
        </Stack>
    );
};
